import { Button, createStyles, makeStyles, Theme } from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Alert } from '@material-ui/lab';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
import { SnackbarProvider } from 'notistack';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Route, Switch } from 'react-router-dom';

import { EnsureSignedIn } from 'components/EnsureSignedIn';
import { analytics } from 'FirebaseConfigs';
import { useAppTheme } from 'hooks/useAppTheme';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { Routes } from 'Routes';

import { AddToHomeScreenProvider } from './components/AddToHomeScreen';
import { AppUserProvider } from './components/AppUserProvider';
import { AuthUserProvider } from './components/AuthUserProvider';
import { LatestOffersProvider } from './components/LatestOffersProvider';
import { OfferDraftsProvider } from './components/OfferDraftsProvider';
import { OrgProvider } from './components/OrgProvider';
import { OrgUserInvitesProvider } from './components/OrgUserInvitesProvider';
import { OrgUserEmailInvitesProvider } from './components/OrgUserEmailInvitesProvider';
import { OrgUserProvider } from './components/OrgUserProvider';
import { OrgUsersProvider } from './components/OrgUsersProvider';
import { PriceFieldProvider } from './components/PriceFieldProvider';
import { PriceFieldSuggestionsProvider } from './components/PriceFieldSuggestionsProvider';
import { Paths } from './Paths';
import { CategoryExplorer } from './screens/Offer/CreateOffer/CategoryExplorer';

import './App.css';
import './FirebaseConfigs';

function Boundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary onError={(error, info) => {
      analytics.logEvent(AnalyticsEvent.ERROR_BOUNDARY_ACTIVATION, { error, info })
      console.error(error)
      console.error(info)
    }} FallbackComponent={({ resetErrorBoundary, error }) => (
      <Alert style={{ width: '100%' }} severity="warning">
        {'Oisann! Her skjedde det noe rart. Trykk'}
        <Button color="primary" onClick={resetErrorBoundary}>her</Button>{' for å prøve på nytt eller'}
        <Button color="primary" onClick={() => window.location.reload()}>her</Button>
        {' for å fornye siden:'}
        {error.message}
      </Alert>
    )}>{children}</ErrorBoundary>
  )
}

export const useStyles = makeStyles((theme: Theme) => createStyles({
  snackbars: {
    top: '60px'
  },
}))

function App() {
  const theme = useAppTheme();
  const ref = React.createRef<SnackbarProvider>()
  const classes = useStyles();

  return (
    <Boundary>
      <BrowserRouter>
        <Boundary>
          <ThemeProvider theme={theme}>
            <Boundary>
              <SnackbarProvider
                preventDuplicate
                ref={ref}
                style={{ top: 200 }}
                classes={{
                  containerRoot: classes.snackbars,
                }}
                action={key => <Button color="inherit" size="small" onClick={() => ref.current?.closeSnackbar(key)}>Ok</Button>}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }} maxSnack={5} dense>
                <Boundary>
                  <AddToHomeScreenProvider>
                    <Boundary>
                      <CssBaseline />
                    </Boundary>
                    <Boundary>
                      <Switch>
                        <Route exact path={Paths.CATEGORY_EXPLORER}>
                          <Boundary>
                            <CategoryExplorer />
                          </Boundary>
                        </Route>
                        <Route path={Paths.ROOT}>
                          <Boundary>
                            <AuthUserProvider>
                              <Boundary>
                                <EnsureSignedIn>
                                  <Boundary>
                                    <AppUserProvider>
                                      <Boundary>
                                        <OrgProvider>
                                          <Boundary>
                                            <OrgUserInvitesProvider>
                                              <OrgUserEmailInvitesProvider>
                                                <Boundary>
                                                  <OrgUserProvider>
                                                    <Boundary>
                                                      <OrgUsersProvider>
                                                        <Boundary>
                                                          <OfferDraftsProvider>
                                                            <Boundary>
                                                              <LatestOffersProvider>
                                                                <Boundary>
                                                                  <PriceFieldProvider>
                                                                    <Boundary>
                                                                      <PriceFieldSuggestionsProvider>
                                                                        <Boundary>
                                                                          <Routes />
                                                                        </Boundary>
                                                                      </PriceFieldSuggestionsProvider>
                                                                    </Boundary>
                                                                  </PriceFieldProvider>
                                                                </Boundary>
                                                              </LatestOffersProvider>
                                                            </Boundary>
                                                          </OfferDraftsProvider>
                                                        </Boundary>
                                                      </OrgUsersProvider>
                                                    </Boundary>
                                                  </OrgUserProvider>
                                                </Boundary>
                                              </OrgUserEmailInvitesProvider>
                                            </OrgUserInvitesProvider>
                                          </Boundary>
                                        </OrgProvider>
                                      </Boundary>
                                    </AppUserProvider>
                                  </Boundary>
                                </EnsureSignedIn>
                              </Boundary>
                            </AuthUserProvider>
                          </Boundary>
                        </Route>
                      </Switch>
                    </Boundary>
                  </AddToHomeScreenProvider>
                </Boundary>
              </SnackbarProvider>
            </Boundary>
          </ThemeProvider>
        </Boundary>
      </BrowserRouter>
    </Boundary>
  );
}

export default App
